import { useCallback, useEffect, useState } from "react";
import { differenceBy, isEqual } from "lodash";

import { useDelay } from "hooks";

export const DND_TYPE = {
  REORDER: "REORDER",
  DRAG_TO_CONDITIONS_GROUP: "DRAG_TO_CONDITIONS_GROUP",
  DRAG_OUT_OF_CONDITIONS_GROUP: "DRAG_OUT_OF_CONDITIONS_GROUP",
};

const initState = { eventType: null, targetGroup: null, target: null };

export const getDraggableTarget = (newList, oldList, isTargetACondition) => {
  if (newList.length === oldList.length) return null;

  const addedItems = newList.length > oldList.length;
  const removedItems = newList.length < oldList.length;

  const target = addedItems
    ? differenceBy(newList, oldList, "id")[0]
    : differenceBy(oldList, newList, "id")[0];

  const shouldFilterCondition = isTargetACondition ? addedItems : removedItems;

  return target && (!shouldFilterCondition || !target.conditions?.length)
    ? target
    : null;
};

export const getDraggableEvent = ({
  newList,
  oldList,
  targetGroup = null,
  draggedItem = null,
}) => {
  if (newList.length === oldList.length)
    return {
      eventType: DND_TYPE.REORDER,
      target: draggedItem,
      targetGroup: null,
    };

  const target = getDraggableTarget(newList, oldList, Boolean(targetGroup));
  if (!target) return null;

  if (newList.length > oldList.length) {
    return {
      eventType: targetGroup
        ? DND_TYPE.DRAG_TO_CONDITIONS_GROUP
        : DND_TYPE.DRAG_OUT_OF_CONDITIONS_GROUP,
      targetGroup: targetGroup,
      target: target,
    };
  }

  return {};
};

export function useDragDropEventManager(
  itemGroups,
  onItemReOrdered,
  itemType = "task"
) {
  const [event, setEvent] = useState({ ...initState, itemGroups });
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    setEvent({ ...initState, itemGroups });
  }, [itemGroups]);

  const delayEvent = useDelay(100);
  const reorderCallback = useCallback(
    (e) => {
      delayEvent(() => {
        if (e.eventType) onItemReOrdered(e);
      });
    },
    [delayEvent, onItemReOrdered]
  );

  useEffect(() => {
    reorderCallback(event);
  }, [event, reorderCallback]);

  return {
    itemList: event.itemGroups,
    setDraggedItem: setDraggedItem,
    modifyRootList: (newOrderedList) => {
      if (isEqual(event.itemGroups, newOrderedList)) return;

      const targetProps = getDraggableEvent({
        newList: newOrderedList,
        oldList: event.itemGroups,
        draggedItem: draggedItem,
      });

      if (!targetProps) return;

      setEvent((curEvent) => {
        return { ...curEvent, ...targetProps, itemGroups: newOrderedList };
      });
    },
    modifySubList: (index, conditionsGroup, newConditionItems) => {
      const conditionItems = conditionsGroup[itemType];
      if (isEqual(conditionItems, newConditionItems)) return;

      const targetProps = getDraggableEvent({
        newList: newConditionItems,
        oldList: conditionItems,
        targetGroup: conditionsGroup,
        draggedItem: draggedItem,
      });

      if (!targetProps) return;

      setEvent((curEvent) => {
        return {
          ...curEvent,
          ...targetProps,
          itemGroups: curEvent.itemGroups.map((t, i) => {
            return i === index ? { ...t, [itemType]: newConditionItems } : t;
          }),
        };
      });
    },
  };
}

export default useDragDropEventManager;
